import { Policy } from "@deathstar/types/northstar";
import {
    ArrowPathIcon,
    ArrowUturnLeftIcon,
    CheckIcon,
    ClockIcon,
    ExclamationTriangleIcon,
    NoSymbolIcon,
    PencilIcon,
    XCircleIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";
import { classNames } from "../classNames/classNames";
import { CertificateIcon } from "../icons/CertificateIcon";

export function PolicyStatus({ policy }: { policy: Policy }): JSX.Element {
    const baseClasses = "rounded-lg border px-2 text-base font-medium small-caps flex w-max items-center gap-2";
    const isDemo = Policy.isDemo(policy);

    if (policy.isDeleted) {
        return (
            <div className={classNames(baseClasses, "border-none bg-red-700 text-white")}>
                <XMarkIcon className="mt-0.5 h-4 w-4" />
                Deleted
            </div>
        );
    }

    if (policy.isCertUseOnly) {
        return (
            <div className={classNames(baseClasses, "border-gray-100 bg-gray-50/50 text-gray-500")}>
                <CertificateIcon className="mt-0.5 h-4 w-4" />
                Cert Use Only
            </div>
        );
    }

    if (policy.status === Policy.Status.DRAFT) {
        return (
            <div
                className={classNames(baseClasses, "bg-sky-50/50 text-sky-500", {
                    "border-sky-100": !isDemo,
                    "border-orange-400": isDemo,
                })}
            >
                <PencilIcon className="mt-0.5 h-4 w-4" />
                Draft
            </div>
        );
    }
    if (policy.status === Policy.Status.REWRITTEN) {
        return (
            <div
                className={classNames(baseClasses, "bg-yellow-50/50 text-yellow-500", {
                    "border-yellow-100": !isDemo,
                    "border-orange-400": isDemo,
                })}
            >
                <ArrowUturnLeftIcon className="mt-0.5 h-4 w-4" />
                Rewritten
            </div>
        );
    }
    if (policy.status === Policy.Status.ACTIVE) {
        return (
            <div
                className={classNames(baseClasses, "bg-emerald-50/50 text-emerald-500", {
                    "border-emerald-100": !isDemo,
                    "border-orange-400": isDemo,
                })}
            >
                <CheckIcon className="mt-0.5 h-4 w-4" />
                Active
            </div>
        );
    }
    if (policy.status === Policy.Status.CANCELLED) {
        return (
            <div
                className={classNames(baseClasses, "bg-red-50/50 text-red-500", {
                    "border-red-100": !isDemo,
                    "border-orange-400": isDemo,
                })}
            >
                <XCircleIcon className="mt-0.5 h-4 w-4" />
                Cancelled
            </div>
        );
    }
    if (policy.status === Policy.Status.NOT_TAKEN) {
        return (
            <div
                className={classNames(baseClasses, "bg-red-50/50 text-red-500", {
                    "border-red-100": !isDemo,
                    "border-orange-400": isDemo,
                })}
            >
                <NoSymbolIcon className="mt-0.5 h-4 w-4" />
                Not taken
            </div>
        );
    }
    if (policy.status === Policy.Status.PRE_CANCELLATION) {
        return (
            <div
                className={classNames(baseClasses, "bg-orange-50/50 text-orange-500", {
                    "border-orange-100": !isDemo,
                    "border-orange-400": isDemo,
                })}
            >
                <ExclamationTriangleIcon className="mt-0.5 h-4 w-4" />
                Pre-cancellation
            </div>
        );
    }
    if (policy.status === Policy.Status.RENEWED) {
        return (
            <div
                className={classNames(baseClasses, "bg-indigo-50/50 text-indigo-500", {
                    "border-indigo-100": !isDemo,
                    "border-orange-400": isDemo,
                })}
            >
                <ArrowPathIcon className="mt-0.5 h-4 w-4" />
                Renewed
            </div>
        );
    }
    if (policy.status === Policy.Status.EXPIRED) {
        return (
            <div
                className={classNames(baseClasses, "bg-red-50/50 text-red-500", {
                    "border-red-100": !isDemo,
                    "border-orange-400": isDemo,
                })}
            >
                <ClockIcon className="mt-0.5 h-4 w-4" />
                Expired
            </div>
        );
    }
    return (
        <div
            className={classNames(baseClasses, "bg-gray-50/50 text-gray-500", {
                "border-gray-100": !isDemo,
                "border-orange-400": isDemo,
            })}
        >
            <ExclamationTriangleIcon className="mt-0.5 h-4 w-4" />
            {policy.status}
        </div>
    );
}
